import React, { useCallback, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { View } from 'react-native';
import { AppView } from 'design-system';
import AmountPill from 'design-system/AmountPill';
import Button from 'design-system/Button';
import MoneyInput from 'design-system/MoneyInput';
import Spacer from 'design-system/Spacer';
import StatusBar from 'design-system/StatusBar';
import { rem } from 'design-system/values';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import createStyleSheets from 'utils/createStyleSheets';
import Text from 'design-system/Text';
import { formatAmount } from 'utils/formatting';
const DEFAULT_MINIMUM_DEPOSIT_AMOUNT = 1;
const DEFAULT_MAXIMUM_DEPOSIT_AMOUNT = 1500;
const DepositSelectCustomAmountScreen = ({ route, navigation, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const moneyInputRef = useRef(null);
    const { title, onContinue, initialAmount, availableAmount, suggestedAmounts, remainingAllowance, min = DEFAULT_MINIMUM_DEPOSIT_AMOUNT, max = DEFAULT_MAXIMUM_DEPOSIT_AMOUNT, } = route.params;
    const getAmountError = useCallback((value) => {
        if (value < min || value > max) {
            return 'outOfRange';
        }
        if (remainingAllowance !== undefined && value > remainingAllowance) {
            return 'exceedAllowance';
        }
        if (availableAmount !== undefined && value > availableAmount) {
            return 'notEnoughFunds';
        }
        return undefined;
    }, [min, max, availableAmount, remainingAllowance]);
    const initial = initialAmount ?? min;
    const amountRef = useRef(initial);
    const [amountError, setAmountError] = useState(() => getAmountError(initial));
    useLayoutEffect(() => {
        navigation.setOptions({
            title: title ?? 'Enter amount',
            headerTintColor: colors.text.primary,
            headerRight: () => <HelpScreenButton />,
        });
    }, [colors.text.primary, title]);
    const moneyInputColors = useMemo(() => ({
        input: colors.text.primary,
        pinpad: colors.text.primary,
        hintColor: colors.text.statusNegative,
    }), [colors.text.primary, colors.text.statusNegative]);
    const onAmountChange = useCallback((value) => {
        amountRef.current = value;
        setAmountError(getAmountError(value));
    }, [getAmountError]);
    const handleContinue = useCallback(() => {
        if (getAmountError(amountRef.current)) {
            moneyInputRef.current?.shake();
            return;
        }
        onContinue(amountRef.current);
        navigation.goBack();
    }, [getAmountError, onContinue]);
    const setAmount = useCallback((amount) => () => {
        moneyInputRef.current?.setAmount(amount);
    }, []);
    const numPadHeader = useMemo(() => suggestedAmounts?.length ? (<View style={styles.numPadHeader}>
          <Spacer h={24}/>
          <View style={styles.amountSelectors}>
            {suggestedAmounts.map((amount) => (<AmountPill selected key={amount} value={amount} currency="GBP" onPress={setAmount(amount)} style={styles.amountPill}/>))}
          </View>
        </View>) : undefined, [
        setAmount,
        suggestedAmounts,
        styles.amountPill,
        styles.amountSelectors,
        styles.numPadHeader,
    ]);
    const paddingBottom = useMarginBottom('paddingBottom', undefined, styles.container);
    const handleMaxExceeded = useCallback(() => {
        setAmountError('exceededMax');
    }, []);
    const hintOverride = useMemo(() => {
        if (amountError) {
            if (amountError === 'exceedAllowance') {
                return (<Text TextThin-14 Negative>
            <Text NumbersThin-14 Negative>
              {formatAmount(remainingAllowance, 'GBP', 2)}
            </Text>{' '}
            left to invest this tax year
          </Text>);
            }
            if (amountError === 'notEnoughFunds') {
                return (<Text TextThin-14 Negative>
            Available{' '}
            <Text NumbersThin-14 Negative>
              {formatAmount(availableAmount, 'GBP', 2)}
            </Text>
          </Text>);
            }
        }
        if (availableAmount !== undefined) {
            return (<Text TextThin-14 Secondary>
          Available{' '}
          <Text NumbersThin-14 Secondary>
            {formatAmount(availableAmount, 'GBP', 2)}
          </Text>
        </Text>);
        }
        if (remainingAllowance !== undefined) {
            return (<Text TextThin-14 Secondary>
          <Text NumbersThin-14 Secondary>
            {formatAmount(remainingAllowance, 'GBP', 2)}
          </Text>{' '}
          left to invest this tax year
        </Text>);
        }
        return undefined;
    }, [amountError, availableAmount, remainingAllowance]);
    return (<AppView type="primary" avoidHeader>
      <View style={paddingBottom}>
        <StatusBar barStyle="dark-content" animated/>
        <MoneyInput flex showMinHint showMaxHint min={min} max={max} ref={moneyInputRef} initialValue={initial} colors={moneyInputColors} restrictAmountsLessThanOne pinPadStyle={styles.pinPad} numPadHeader={numPadHeader} onValueChange={onAmountChange} alwaysDisplayMinHint={false} alwaysDisplayMaxHint={false} hintOverride={hintOverride} onExceedMax={handleMaxExceeded}/>
        <Spacer h={30}/>
        <View style={styles.buttonContainer}>
          <Button brand title="Continue" onPress={handleContinue} disabled={!!amountError && amountError !== 'exceededMax'}/>
        </View>
      </View>
    </AppView>);
};
const styleSet = createStyleSheets(() => ({
    container: {
        flex: 1,
    },
    buttonContainer: {
        paddingHorizontal: rem(16),
    },
    numPadHeader: {
        flex: 2,
        width: '100%',
    },
    amountSelectors: {
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'center',
    },
    amountPill: {
        marginHorizontal: rem(4),
    },
    pinPad: {
        marginTop: rem(10),
    },
}));
export default DepositSelectCustomAmountScreen;
